import { h } from 'preact'; // h is required for JSX transform
import { OrthomolIcon } from '../../components-common/OrthomolIcon';
import { CartImage } from './CartImage';
import type { LineItem } from '../shopware/shopwareApi';
import { getFormattedLineItemPrices, formatPrice } from '../shopware/shopwareUtils';
import { URLS } from '../Urls';
import { useJsonProvider } from '../JsonProvider';
import { CartItemAboLabel } from './CartItemAboLabel';
import { getDeliveryIntervalTextFromProductNumber } from '../utils/subscriptionUtils';

const QUANTITY_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

interface CartProductListItemProps {
  lineItem: LineItem;
  mode?: 'drawer' | 'page';
  hideOnMobile?: boolean;
  onRemove?: () => void;
  onQuantityChange?: (quantity: number) => void;
}

export const CartProductListItem = ({
  lineItem,
  mode = 'drawer',
  hideOnMobile = false,
  onRemove,
  onQuantityChange,
}: CartProductListItemProps) => {
  const lineItemId = lineItem.id || '';
  const isPromotion = lineItem.type === 'promotion';

  // Extract Hauptprodukt, Darreichungsform and Packsize from options
  const options = lineItem.payload?.options || [];
  const hauptprodukt = options.find((opt: any) => opt.group === 'Hauptprodukt')?.option || lineItem.label || 'Produkt';
  const darreichungsform = options.find((opt: any) => opt.group === 'Darreichungsform')?.option;
  const packsize = options.find((opt: any) => opt.group === 'Packungsgröße')?.option;

  const name = hauptprodukt;
  const variant = darreichungsform && packsize ? `${darreichungsform} ${packsize}` : darreichungsform;

  // For plan (subscription) items, use real_product_number from customFields for image/redirect
  const payloadCustomFields = lineItem.payload?.customFields as any;
  const isPlan = payloadCustomFields?.is_plan === true;
  const hideBasePrice = payloadCustomFields?.hide_base_price === true;
  const baseProductNumber = isPlan
    ? (payloadCustomFields?.real_product_number ?? lineItem.payload?.productNumber)
    : lineItem.payload?.productNumber;
  const url = baseProductNumber ? URLS.productRedirectWithONumber(baseProductNumber) : '#';
  const quantity = lineItem.quantity || 1;

  // Get all formatted prices using shopwareUtils helpers
  const { unitPrice, totalPrice, referencePrice, listPrice: rawListPrice, totalListPrice: rawTotalListPrice } =
    getFormattedLineItemPrices(lineItem);

  // For plan items: use base product price from lookup as Streichpreis
  const { baseProductPrices } = useJsonProvider();
  const basePriceForPlan = isPlan && baseProductNumber ? baseProductPrices.data?.[baseProductNumber] : undefined;
  const listPrice = isPlan && basePriceForPlan != null
    ? `${formatPrice(basePriceForPlan)} €`
    : rawListPrice;
  const totalListPrice = isPlan && basePriceForPlan != null
    ? `${formatPrice(basePriceForPlan * quantity)} €`
    : rawTotalListPrice;

  // For promotions: check if type is percentage, then display percentage only, otherwise display totalPrice
  const priceDefinition = lineItem.priceDefinition as any;
  const isPercentagePromotion =
    isPromotion && priceDefinition?.type === 'percentage' && priceDefinition.percentage !== undefined;
  const promotionDisplay = isPercentagePromotion ? `${priceDefinition.percentage}%` : totalPrice;

  // Get available stock from lineItem.payload.stock (ignored for plan items)
  const availableStock = lineItem.payload?.stock as number | undefined;
  const maxQuantity = isPlan || availableStock === undefined ? 30 : Math.min(availableStock, 30);

  // Build className with conditional mobile hide class
  const mobileHideClass = hideOnMobile ? ' d-none d-md-flex' : '';

  if (mode === 'page') {
    // page mode
    if (isPromotion) {
      // Promotion/Voucher line item
      return (
        <li
          className={`bg-white d-flex flex-column flex-md-row gap-2 gap-md-4 align-items-md-center position-relative p-3${mobileHideClass}`}
          id={`shopping-cart-item-${lineItemId}`}
          aria-labelledby="legend-headline-product"
        >
          {/* promotion code with icon */}
          <div className="w-100 px-md-0 pe-4 pe-md-0 d-flex align-items-center flex-md-row flex-md-grow-1 gap-2 gap-md-0">
            <span className="text-primary">
              <OrthomolIcon name="voucher-24" size="24" ariaHidden={true} style={{ marginTop: '-2px' }} />
            </span>
            <h6 className="text-primary mb-0 flex-grow-1 ms-md-2">{name}</h6>
            {/* promotion display - shown on mobile in same line, in columns on desktop */}
            <strong className="shopping-cart-item-promotion text-nowrap d-md-none" style={{ marginTop: '-2px' }}>
              {promotionDisplay}
            </strong>
          </div>
          {/* empty price column (no unit price for promotions) - desktop only */}
          <p
            className="shopping-cart-item-price small mb-0 text-md-end px-md-0 d-none d-md-block"
            aria-labelledby="legend-headline-price"
          >
            {/* Empty - promotions don't have unit price */}
          </p>
          {/* empty quantity column (no quantity for promotions) - desktop only */}
          <div className="shopping-cart-item-amount flex-shrink-0 px-md-0 text-center d-none d-md-block">
            {/* Empty - promotions don't show quantity */}
          </div>
          {/* total - displayed in red for promotions - desktop only */}
          <p
            className="small shopping-cart-item-total shopping-cart-item-promotion flex-shrink-0 me-md-5 mb-0 px-md-0 d-none d-md-block"
            aria-labelledby="legend-headline-total"
            style={{ paddingTop: '2px' }}
          >
            <strong>
              <span id={`shopping-cart-item-${lineItemId}-total`}>{promotionDisplay}</span>
            </strong>
          </p>
          {/* remove button */}
          <button
            aria-label={`${name} aus dem Warenkorb entfernen`}
            className="shopping-cart-overview-items-remove btn text-primary p-0 border-0 m-3 m-md-4 bg-transparent"
            onClick={onRemove}
          >
            <OrthomolIcon name="cross-24" size="16" />
          </button>
        </li>
      );
    }

    // Regular product line item
    return (
      <li
        className={`bg-white d-flex flex-column flex-md-row gap-1 gap-md-4 align-items-md-center position-relative p-3${mobileHideClass}`}
        id={`shopping-cart-item-${lineItemId}`}
        aria-labelledby="legend-headline-product"
      >
        {/* product packshot */}
        <div className="shopping-cart-item-visual flex-shrink-0 px-2 px-md-0" aria-hidden="true">
          <a href={url} tabIndex={-1}>
            <CartImage oNumberOrUniquePacksizeId={baseProductNumber} mode="json" />
          </a>
        </div>
        {/* product name + dosage */}
        <div className="w-100 ps-7 pe-4 px-md-0">
          <h6 className="text-primary mb-0">
            <a href={url} className="text-decoration-none">
              {name}
            </a>
          </h6>
          <p className="small mb-0">{variant}</p>
        </div>
        {/* price – unit price hidden on mobile, only Gesamtpreis column is shown there */}
        <p
          className="shopping-cart-item-price small mb-0 ps-7 pe-4 px-md-0 d-none d-md-flex justify-content-end"
          aria-labelledby="legend-headline-price"
        >
          <span className="d-flex flex-column gap-1 align-items-end">
            <span className="d-flex gap-2 align-items-baseline">
              {listPrice && <s className="price-original">{listPrice}</s>}
              <span className={`text-nowrap ${listPrice ? 'price-discounted' : ''}`}>{unitPrice}</span>
              {!hideBasePrice && referencePrice && <small className="text-nowrap">({referencePrice})</small>}
            </span>
            {isPlan && <CartItemAboLabel deliveryText={getDeliveryIntervalTextFromProductNumber(lineItem.payload?.productNumber)} />}
          </span>
        </p>
        {/* product amount */}
        <label
          className="shopping-cart-item-amount flex-shrink-0 ps-7 pe-4 px-md-0"
          aria-labelledby="legend-headline-amount"
        >
          <select
            className="form-control small lh-1 bg-transparent"
            value={quantity}
            onChange={(e) => onQuantityChange?.(parseInt((e.target as HTMLSelectElement).value, 10))}
          >
            {QUANTITY_OPTIONS.map((i) => (
              <option key={i} value={i} disabled={i > maxQuantity}>
                {i}
              </option>
            ))}
          </select>
        </label>
        {/* total – on mobile includes strikethrough list price since unit price column is hidden */}
        <p
          className="small shopping-cart-item-total flex-shrink-0 me-md-5 mb-0 ps-7 pe-4 px-md-0"
          aria-labelledby="legend-headline-total"
        >
          {totalListPrice && <s className="price-original me-2 d-md-none">{totalListPrice}</s>}
          <strong>
            <span id={`shopping-cart-item-${lineItemId}-total`} className="d-none d-md-inline text-nowrap">
              {totalPrice}
            </span>
            <span className={`d-md-none text-nowrap ${totalListPrice ? 'price-discounted' : ''}`}>{totalPrice}</span>
          </strong>
          {isPlan && <span className="d-block d-md-none"><CartItemAboLabel deliveryText={getDeliveryIntervalTextFromProductNumber(lineItem.payload?.productNumber)} /></span>}
          {!hideBasePrice && referencePrice && <small className="d-block d-md-none text-nowrap fw-light">({referencePrice})</small>}
        </p>
        {/* remove button */}
        <button
          aria-label={`${name} aus dem Warenkorb entfernen`}
          className="shopping-cart-overview-items-remove btn text-primary p-0 border-0 m-3 m-md-4 bg-transparent"
          onClick={onRemove}
        >
          <OrthomolIcon name="cross-24" size="16" />
        </button>
      </li>
    );
  }

  // drawer mode
  if (isPromotion) {
    // Promotion/Voucher line item
    return (
      <li className={`shopping-cart-item bg-gray-200 d-flex gap-2 px-3 py-3${mobileHideClass}`}>
        <div className="d-flex align-items-center gap-2 w-100">
          <span className="text-primary">
            <OrthomolIcon name="voucher-24" size="24" ariaHidden={true} style={{ marginTop: '-1px' }} />
          </span>
          <h6 className="text-primary mb-0 lh-1 flex-grow-1 ms-2">{name}</h6>
          <strong className="shopping-cart-item-promotion lh-1 text-nowrap">{promotionDisplay}</strong>
        </div>
      </li>
    );
  }

  // Regular product line item
  return (
    <li className={`shopping-cart-item bg-gray-200 d-flex gap-4 px-4 py-3${mobileHideClass}`}>
      <div className="shopping-cart-item-visual flex-shrink-0" aria-hidden="true">
        <a href={url} tabIndex={-1}>
          <CartImage oNumberOrUniquePacksizeId={baseProductNumber} mode="json" />
        </a>
      </div>
      <div className="d-flex flex-column gap-1">
        <h6 className="text-primary mb-0 lh-1">
          <a href={url} className="text-decoration-none">
            {name}
          </a>
        </h6>
        <small>{variant}</small>
        <p className="product-variant-priceinfo mb-0">
          <strong className="lh-1 text-nowrap">
            <span className="d-none d-md-inline">
              {listPrice && <s className="price-original me-2">{listPrice}</s>}
              <span className={listPrice ? 'price-discounted' : ''}>{unitPrice}</span>
            </span>
            <span className="d-md-none">
              {totalListPrice && <s className="price-original me-2">{totalListPrice}</s>}
              <span className={totalListPrice ? 'price-discounted' : ''}>{totalPrice}</span>
            </span>
          </strong>
        </p>
        {isPlan && <CartItemAboLabel deliveryText={getDeliveryIntervalTextFromProductNumber(lineItem.payload?.productNumber)} />}
        {!hideBasePrice && referencePrice && <small className="text-nowrap fw-light mb-1">({referencePrice})</small>}
        <label className="shopping-cart-item-amount position-relative">
          <span className="position-absolute small lh-1">Menge:</span>
          <select
            className="form-control small lh-1 bg-transparent"
            value={quantity}
            onChange={(e) => onQuantityChange?.(parseInt((e.target as HTMLSelectElement).value, 10))}
          >
            {QUANTITY_OPTIONS.map((num) => (
              <option key={num} value={num} disabled={num > maxQuantity}>
                {num}
              </option>
            ))}
          </select>
        </label>
      </div>
    </li>
  );
};
